import React, { useEffect, useRef, useState } from 'react';
import { playTypewriterSound, stopTypewriterSound } from '../../../../utils/sounds';

export interface FastTypingHostProps {
  text: string;
  timeLimit: number;
  /** When true, stop typewriter animation and sound (e.g. everyone submitted). */
  roundEnded?: boolean;
  /** Delay in ms between revealing each character */
  charDelayMs?: number;
}

/**
 * Host view: typewriter animation revealing the text character by character,
 * with optional typing sound. After reveal completes, full text stays visible.
 */
export const FastTypingHost: React.FC<FastTypingHostProps> = ({
  text,
  timeLimit,
  roundEnded = false,
  charDelayMs = 80,
}) => {
  const [visibleLength, setVisibleLength] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Stop typewriter and sound when round ends (e.g. everyone submitted)
  useEffect(() => {
    if (!roundEnded) return;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    stopTypewriterSound();
  }, [roundEnded]);

  useEffect(() => {
    if (!text || timeLimit <= 0 || roundEnded) return;
    setVisibleLength(0);

    // Play typing sound once when typewriter starts; stop it when round ends or text finishes
    playTypewriterSound();

    intervalRef.current = setInterval(() => {
      setVisibleLength((prev) => {
        const next = prev + 1;
        if (next >= text.length && intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          stopTypewriterSound();
        }
        return Math.min(next, text.length);
      });
    }, charDelayMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      stopTypewriterSound();
    };
  }, [text, timeLimit, roundEnded, charDelayMs]);

  const visibleText = text.slice(0, visibleLength);
  const cursor = visibleLength < text.length ? '|' : '';

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flex: '1 1 auto',
        minHeight: 0,
        padding: '0.5rem 1rem',
      }}
    >
      <div
        className="fast-typing-host-text"
        style={{
          width: '100%',
          maxWidth: 720,
          padding: '1.25rem 1.5rem',
          fontFamily: 'ui-monospace, monospace',
          fontSize: 'clamp(1.25rem, 2.8vw, 1.6rem)',
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          background: 'linear-gradient(135deg, rgba(var(--color-primary-rgb), 0.55) 0%, rgba(var(--color-secondary-rgb), 0.62) 100%)',
          borderRadius: 12,
          border: '2px solid rgba(var(--color-primary-rgb), 0.45)',
          boxShadow: 'var(--shadow-sm)',
          textAlign: 'left',
        }}
      >
        {visibleText}
        <span
          className="fast-typing-cursor"
          style={{
            opacity: visibleLength < text.length ? 1 : 0,
            animation: visibleLength < text.length ? 'fast-typing-blink 0.8s ease-in-out infinite' : 'none',
          }}
        >
          {cursor}
        </span>
      </div>
      <style>{`
        @keyframes fast-typing-blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0.3; }
        }
        @media (min-width: 1024px) {
          .fast-typing-host-text { font-size: 1.95rem; }
        }
        @media (min-width: 1440px) {
          .fast-typing-host-text { font-size: 2.85rem; }
        }
      `}</style>
    </div>
  );
};
